/**
 * Zoho Meeting's OAuth redirect lands on ONE registered URL — `https://app.<APP_DOMAIN>/settings/
 * integrations/zoho/callback` (Zoho allows no wildcards) — but the admin who started the connection
 * lives on their company's own subdomain, and a session is stored per origin. The API therefore
 * prefixes the `state` it hands Zoho with the company slug: `<orgSlug>~<signedState>`. The callback
 * path is public (no session exists on the central origin); before anything else it asks this
 * helper whether the page belongs on a tenant host, and if so bounces there with the query string
 * untouched, where the normal signed-in flow completes the connection.
 *
 * NO OPEN REDIRECT. The target host is built only from `orgSubdomainOrigin(slug)` for a slug that
 * passed `orgSlugSchema` ([a-z0-9-], not reserved) — never from a URL or parameter value. The path
 * and query are appended to that fixed origin, so the worst a crafted link can do is send the
 * visitor to some company's own callback page with parameters the API will reject.
 *
 * A bare state with no `<slug>~` prefix (an attempt begun before the prefix existed) renders here,
 * as does anything already on the right tenant host.
 */

import { extractOrgSlugFromHost, orgSlugSchema } from '@hrms/shared';
import { APP_DOMAIN, isDevOffAppDomain, orgSubdomainOrigin } from './subdomain.js';

/** The slug named by a `<slug>~<signedState>` state, or null when it carries none that is valid. */
export function zohoStateOrgSlug(state: string | null): string | null {
  if (!state) return null;
  const separator = state.indexOf('~');
  // A prefix must be non-empty and followed by a signed state.
  if (separator <= 0 || separator === state.length - 1) return null;
  const parsed = orgSlugSchema.safeParse(state.slice(0, separator));
  return parsed.success ? parsed.data : null;
}

/**
 * `null` to render the callback on this origin; otherwise the absolute URL to `location.replace`.
 * Local development has no subdomain to hop to, so it never bounces.
 */
export function zohoCallbackBounceUrl(location: {
  hostname: string;
  pathname: string;
  search: string;
}): string | null {
  const host = location.hostname.toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1' || isDevOffAppDomain()) return null;

  const slug = zohoStateOrgSlug(new URLSearchParams(location.search).get('state'));
  if (!slug) return null;
  if (extractOrgSlugFromHost(host, APP_DOMAIN) === slug) return null;

  return `${orgSubdomainOrigin(slug)}${location.pathname}${location.search}`;
}
